import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AlertTriangle, Clock, CheckCircle, X } from 'lucide-react';
import { RootState, AppDispatch } from '../../../store';
import { fetchAtRiskDependencies } from '../../../store/slices/dependenciesSlice';
import Button from '../../../components/ui/Button';
import Badge from '../../../components/ui/Badge';
import { DependencySeverity } from '../../../types';

export const DependencyAlerts: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { dependencies, summary } = useSelector((state: RootState) => state.dependencies);

  useEffect(() => {
    // Fetch at-risk dependencies for alerts
    dispatch(fetchAtRiskDependencies());
  }, [dispatch]);

  // Filter for the most critical alerts
  const criticalAlerts = dependencies.filter(dep => 
    dep.severity === DependencySeverity.CRITICAL && 
    (dep.riskLevel === 'OVERDUE' || dep.riskLevel === 'DUE_SOON')
  ).slice(0, 3); // Show only top 3

  const highPriorityAlerts = dependencies.filter(dep => 
    dep.severity === DependencySeverity.HIGH && 
    dep.riskLevel === 'OVERDUE'
  ).slice(0, 2); // Show only top 2

  const allAlerts = [...criticalAlerts, ...highPriorityAlerts];

  if (allAlerts.length === 0) {
    return null;
  }

  const getAlertIcon = (riskLevel: string) => {
    switch (riskLevel) {
      case 'OVERDUE':
        return <AlertTriangle className="w-4 h-4 text-status-error" />;
      case 'DUE_SOON':
        return <Clock className="w-4 h-4 text-status-warning" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-status-warning" />;
    }
  };

  const getAlertColor = (severity: DependencySeverity, riskLevel: string) => {
    if (severity === DependencySeverity.CRITICAL) {
      return riskLevel === 'OVERDUE' ? 'bg-status-error/10 border-status-error/30' : 'bg-status-error/5 border-status-error/20';
    }
    return 'bg-orange-500/10 border-orange-500/20';
  };

  const formatEntityType = (entityType: string) => {
    return entityType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-foreground flex items-center space-x-2">
          <AlertTriangle className="w-5 h-5 text-status-error" />
          <span>Dependency Alerts</span>
          <Badge variant="danger" className="ml-2">
            {allAlerts.length}
          </Badge>
        </h3>
        
        {summary && summary.atRisk > allAlerts.length && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              // This would navigate to the full at-risk view
              console.log('View all at-risk dependencies');
            }}
          >
            View All ({summary.atRisk})
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {allAlerts.map((dependency) => (
          <div
            key={dependency.id}
            className={`p-4 rounded-lg border ${getAlertColor(dependency.severity, dependency.riskLevel || '')}`}
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                {getAlertIcon(dependency.riskLevel || '')}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <h4 className="font-medium text-foreground truncate">
                    {dependency.name}
                  </h4>
                  <Badge 
                    variant={dependency.severity === DependencySeverity.CRITICAL ? 'danger' : 'warning'}
                    className="flex-shrink-0"
                  >
                    {dependency.severity}
                  </Badge>
                </div>
                
                <p className="text-sm text-foreground mb-2">
                  {dependency.description || 'No description provided'}
                </p>
                
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-4 text-muted-foreground">
                    <span>
                      <span className="font-medium text-foreground">{dependency.sourceName}</span>
                      <span className="mx-1">→</span>
                      <span className="font-medium text-foreground">{dependency.targetName}</span>
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {dependency.expectedDate && (
                      <span className={`text-xs ${
                        dependency.riskLevel === 'OVERDUE' ? 'text-status-error' : 'text-status-warning'
                      }`}>
                        {dependency.riskLevel === 'OVERDUE' 
                          ? `${Math.abs(dependency.daysUntilDue || 0)} days overdue`
                          : `Due in ${dependency.daysUntilDue || 0} days`
                        }
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    // This would dismiss the alert or navigate to dependency details
                    console.log('Handle alert action for:', dependency.id);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="flex items-center space-x-2 mt-3 pt-3 border-t border-border/50">
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => {
                  // Navigate to dependency details
                  console.log('View dependency details:', dependency.id);
                }}
              >
                View Details
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => {
                  // Navigate to source entity
                  console.log('View source entity:', dependency.sourceType, dependency.sourceId);
                }}
              >
                View {formatEntityType(dependency.sourceType)}
              </Button>
              
              {dependency.riskLevel === 'OVERDUE' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs text-status-success border-status-success/20 hover:bg-status-success/10"
                  onClick={() => {
                    // Mark as resolved
                    console.log('Mark as resolved:', dependency.id);
                  }}
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Mark Resolved
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {/* Summary Footer */}
      {summary && (
        <div className="bg-muted/30 rounded-lg p-3 border border-border/50">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-4 text-muted-foreground">
              <span>Total at Risk: <span className="text-foreground font-medium">{summary.atRisk}</span></span>
              <span>Critical: <span className="text-status-error font-medium">{summary.bySeverity?.CRITICAL || 0}</span></span>
              <span>High: <span className="text-status-warning font-medium">{summary.bySeverity?.HIGH || 0}</span></span>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                // Refresh alerts
                dispatch(fetchAtRiskDependencies());
              }}
            >
              Refresh
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};